import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import {
    createOutreachRule,
    deleteOutreachRule,
    getOutreachRules,
    getProjectStages,
    updateOutreachRule,
} from "../app/apis/subRatingsAPI";
import { IApiErrorBody, IErrorState } from "../app/interfaces/IApiResponses";
import { IOutreachRule, IOutreachRuleSubmission, IProjectStage } from "../app/interfaces/IOutreachRule";
import { RootState } from "../app/store";

export interface IOutreachRulesSlice {
    status: 'idle' | 'loading' | 'failed';
    data: Array<IOutreachRule>;
    stages: Array<IProjectStage>;
    error: undefined | IErrorState;
}

const initialState: IOutreachRulesSlice = {
    status: 'idle',
    data: [],
    stages: [],
    error: undefined,
};

export const getOutreachRulesThunk = createAsyncThunk(
    'outreachRules/getOutreachRules',
    async ({ companyId, projectId }: { companyId: number, projectId?: number }, { rejectWithValue }) => {
        try {
            return await getOutreachRules(companyId, projectId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const getProjectStagesThunk = createAsyncThunk(
    'outreachRules/getProjectStages',
    async (companyId: number, { rejectWithValue }) => {
        try {
            return await getProjectStages(companyId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const createOutreachRuleThunk = createAsyncThunk(
    'outreachRules/createOutreachRule',
    async ({ companyId, rule }: { companyId: number, rule: IOutreachRuleSubmission }, { rejectWithValue }) => {
        try {
            return await createOutreachRule(companyId, rule);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const updateOutreachRuleThunk = createAsyncThunk(
    'outreachRules/updateOutreachRule',
    async (
        { companyId, ruleId, rule }: { companyId: number, ruleId: number, rule: IOutreachRuleSubmission },
        { rejectWithValue }
    ) => {
        try {
            return await updateOutreachRule(companyId, ruleId, rule);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const deleteOutreachRuleThunk = createAsyncThunk(
    'outreachRules/deleteOutreachRule',
    async ({ companyId, ruleId }: { companyId: number, ruleId: number }, { rejectWithValue }) => {
        try {
            return await deleteOutreachRule(companyId, ruleId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const outreachRulesSlice = createSlice({
    name: 'outreachRules',
    initialState,
    reducers: {
        clearOutreachRulesError: (state) => {
            state.error = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getOutreachRulesThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getOutreachRulesThunk.fulfilled, (state, action) => {
                state.status = 'idle';
                state.data = action.payload as Array<IOutreachRule>;
            })
            .addCase(getOutreachRulesThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(getProjectStagesThunk.fulfilled, (state, action) => {
                state.stages = action.payload as Array<IProjectStage>;
            })
            .addCase(getProjectStagesThunk.rejected, (state, action) => {
                state.error = action?.payload as IErrorState;
            })
            .addCase(createOutreachRuleThunk.pending, (state) => {
                state.status = 'loading';
                state.error = undefined;
            })
            .addCase(createOutreachRuleThunk.fulfilled, (state, action) => {
                state.status = 'idle';
                state.data = [...state.data, action.payload as IOutreachRule];
            })
            .addCase(createOutreachRuleThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(updateOutreachRuleThunk.pending, (state) => {
                state.status = 'loading';
                state.error = undefined;
            })
            .addCase(updateOutreachRuleThunk.fulfilled, (state, action) => {
                const updated = action.payload as IOutreachRule;
                state.status = 'idle';
                state.data = state.data.map(rule => rule.id === updated.id ? updated : rule);
            })
            .addCase(updateOutreachRuleThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(deleteOutreachRuleThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteOutreachRuleThunk.fulfilled, (state, action) => {
                const { id } = action.payload as { id: number };
                state.status = 'idle';
                state.data = state.data.filter(rule => rule.id !== id);
            })
            .addCase(deleteOutreachRuleThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action?.payload as IErrorState;
            })
    }
});

export const { clearOutreachRulesError } = outreachRulesSlice.actions;
export const selectOutreachRulesState = (state: RootState) => state.outreachRules;
export default outreachRulesSlice.reducer;
